import {useCallback, useMemo} from 'react';
import {useFormikContext} from 'formik';
import {
    Drawer,
    DrawerFields,
    HardwareType,
} from 'components/customer/Product/entity/Drawer';
import {DrawerParameters} from 'components/customer/Product/entity/DrawerParameters';
import {
    useGetDrawerSystemQuery,
    useGetRunnerQuery,
} from 'components/customer/Product/store/drawerRunnerApi';
import {useDrawerDefaults} from 'components/customer/Product/Drawer/DrawerDefaultsContext';
import {useAppSelector} from 'store/customer';
import {useInnerDrawerGeometry} from 'components/customer/Product/Drawer/helpers/useInnerDrawerGeometry';
import {
    cabinetDefault,
    toNumber,
} from 'components/customer/Product/Drawer/helpers';
import {useRunnerRequestParams} from 'components/customer/Product/Drawer/helpers/useRunnerRequestParams';
import {
    useValidationTopPart,
    withinCount,
} from 'components/customer/Product/Drawer/helpers/useInnerDrawerLimits';
import {
    FreeSpace,
    freeSpaces,
    insertDrawer,
} from 'components/customer/Product/Drawer/helpers/innerDrawerSlots';

// Where a new drawer of the cabinet's default type can go without moving the
// others, and the drawers once it is placed in one of those spaces.
export const useInnerDrawerSpaces = (drawerParams: DrawerParameters) => {
    const {values} = useFormikContext<DrawerFields>();
    const {defaults} = useDrawerDefaults();
    const {data: drawerSystems} = useGetDrawerSystemQuery();
    const topPart = useValidationTopPart();
    const {carcaseThickness, isRailOnFlat, isRailOnEdge, railWidth} =
        useAppSelector((state) => state.productData);
    const geometry = useInnerDrawerGeometry();
    const usableHeight = geometry
        ? geometry.ceiling - geometry.floor
        : Infinity;
    const typeField = drawerParams.drawerTypeFieldName;
    const runnerField = drawerParams.drawerRunnerFieldName;

    const drawers = useMemo(
        () => withinCount(values.drawers, values.drawer_amount),
        [values.drawers, values.drawer_amount]
    );

    const type =
        cabinetDefault(drawers, typeField, defaults.type) ||
        String(drawers[0]?.[typeField as keyof Drawer] ?? '');
    const system = drawerSystems?.find(
        (candidate) => String(candidate.id) === type
    );

    // a type's runners differ in length, so they are asked for as a drawer of
    // that type would ask, with room for every height
    const ofType = drawers.findIndex(
        (drawer) => String(drawer?.[typeField as keyof Drawer]) == type
    );
    const {params} = useRunnerRequestParams(
        Math.max(0, ofType),
        true,
        false,
        typeField,
        drawerParams.maxRunnerDepth,
        'drawer_face_height',
        drawerParams.fingerPullFieldName
    );
    const everyHeight = useMemo(
        () => ({
            ...params,
            insertType: toNumber(type),
            faceHeight: toNumber(values.cabinet_height),
        }),
        [params, type, values.cabinet_height]
    );
    const {currentData: runners} = useGetRunnerQuery(everyHeight, {
        skip:
            params.depth === undefined ||
            !(everyHeight.insertType > 0) ||
            !(everyHeight.faceHeight > 0),
    });

    const clearances = useMemo(
        () =>
            system
                ? {
                      top: toNumber(system.topClearance),
                      bottom: toNumber(system.bottomClearance),
                      innerBottom: toNumber(system.innerDrawerBottomClearance),
                  }
                : null,
        [system]
    );

    // what the layout shares out (its getTotalAvailableHeight), from the top
    // and thickness it puts in the store
    const availableHeight =
        toNumber(values.cabinet_height) -
        toNumber(carcaseThickness) -
        (isRailOnFlat
            ? toNumber(carcaseThickness)
            : isRailOnEdge
              ? toNumber(railWidth)
              : 0);

    // until the runners arrive, no space means nothing yet, not no room
    const known = !!(clearances && runners?.length && drawers.length);
    const spaces = useMemo(
        () =>
            known
                ? freeSpaces({
                      drawers,
                      cabinetHeight: toNumber(values.cabinet_height),
                      topPart,
                      availableHeight,
                      usableHeight,
                      clearances,
                      runners,
                  })
                : [],
        [
            known,
            drawers,
            values.cabinet_height,
            topPart,
            availableHeight,
            usableHeight,
            clearances,
            runners,
        ]
    );

    // a copy of the drawer whose slot it takes, as the count copies the bottom
    // one, on the default type and the space's runner; -1 is a drawer not
    // saved yet
    const place = useCallback(
        (space: FreeSpace) => {
            const template = drawers[Math.min(space.index, drawers.length - 1)];
            const {runner} = space;
            const added: Drawer = {
                ...template,
                id: -1,
                [typeField]: toNumber(type),
                insert: {id: toNumber(type)},
                systemTopClearance: clearances.top,
                systemBottomClearance: clearances.bottom,
                systemInnerDrawerBottomClearance: clearances.innerBottom,
                [runnerField]: runner.id,
                drawer_runner_height: runner.runnerHeight,
                drawer_runner_depth: runner.runnerDepth,
                drawer_runner_desc: runner.runnerDesc,
                hardware: [
                    {
                        hardware_id: runner.hardware_id,
                        hardware: {type: HardwareType.RUNNER},
                    },
                ],
            };

            if ('drawer_type' in template) added.drawer_type = toNumber(type);

            return [
                ...insertDrawer(drawers, space, added),
                ...(values.drawers ?? []).slice(drawers.length),
            ];
        },
        [drawers, values.drawers, typeField, runnerField, type, clearances]
    );

    return {spaces, place, known};
};
